import React from "react";
import ReactSelect from "react-select";
import { useFormContext, useController } from "react-hook-form";

import { PaziaTooltip } from "../PaziaTooltip/PaziaTooltip";
import { InputErrorMessage } from "../InputErrorMessage/InputErrorMessage";
import { InputLabel } from "../InputLabel/InputLabel";
import { selectBaseClassNames } from "./styles";

export interface IDropdownOption {
  label: string;
  value: string | number;
}
export interface SelectProps extends PaziaSelectProps {
  value?: any;
  key?: React.Key;
  isClearable?: boolean;
  tooltip?: string;
  isLoading?: boolean;
  components?: any;
  hideIndicator?: boolean;
  /** Shown when there is nothing to pick — say where the missing items come from. */
  noOptionsMessage?: string;
}

export const Select = ({
  label,
  name,
  onChange,
  className,
  disabled,
  key,
  value,
  options,
  tooltip,
  required,
  isLoading = false,
  components,
  isClearable = true,
  hideIndicator = false,
  placeholder = "Wybierz...",
  noOptionsMessage = "Brak wyników",
}: SelectProps): JSX.Element => {
  return (
    <div
      data-testid={`pazia-select-${name}`}
      className={`w-full flex ${label ? "flex-col" : ""} ${className ?? ""}`}
    >
      <InputLabel label={label} required={required} name={name} />
      <div
        className="w-full flex gap-2"
        onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) =>
          e.key === "Enter" && e.preventDefault()
        }
      >
        <ReactSelect
          isSearchable={true}
          key={key}
          isDisabled={disabled}
          components={components}
          isClearable={isClearable}
          classNames={selectBaseClassNames(disabled)}
          inputId={name}
          isLoading={isLoading}
          value={options.find((x) => x.value == value) ?? null}
          defaultValue={options.find((x) => x.value == value)}
          menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
          menuPosition="fixed"
          menuShouldScrollIntoView={false}
          styles={{
            control: (provided) => ({
              ...provided,
              boxShadow: "none",
            }),
            container: (provided) => ({
              ...provided,
              width: "100%",
            }),
            indicatorsContainer: (provided) => ({
              ...provided,
              display: hideIndicator ? "none" : "flex",
            }),
            menuPortal: (provided) => ({
              ...provided,
              zIndex: 9999,
            }),
          }}
          onChange={onChange}
          name={name}
          placeholder={placeholder}
          noOptionsMessage={() => noOptionsMessage}
          options={options}
        />
        {tooltip && <PaziaTooltip description={tooltip} />}
      </div>
    </div>
  );
};

export interface PaziaSelectProps {
  name: string;
  options: IDropdownOption[];
  label?: string;
  required?: boolean;
  className?: string;
  tooltip?: string;
  disabled?: boolean;
  isLoading?: boolean;
  hideErrors?: boolean;
  components?: any;
  onChange?: (event: any) => void;
  isClearable?: boolean;
  hideIndicator?: boolean;
  placeholder?: string;
  /** Shown when there is nothing to pick — say where the missing items come from. */
  noOptionsMessage?: string;
}

export const PaziaSelect = ({
  name,
  options,
  label,
  required,
  className,
  disabled,
  hideErrors,
  isLoading,
  isClearable,
  tooltip,
  components,
  onChange,
  hideIndicator = false,
  placeholder,
  noOptionsMessage,
}: PaziaSelectProps): JSX.Element => {
  const { control } = useFormContext();
  const { field } = useController({
    name,
    control,
  });

  const onChangeHandler = (opt: IDropdownOption) => {
    field.onChange(opt?.value ?? null);
    if (onChange) onChange(opt);
  };

  return (
    <div className={className ?? ""}>
      <Select
        onChange={onChangeHandler}
        label={label}
        options={options}
        tooltip={tooltip}
        disabled={disabled}
        isLoading={isLoading}
        required={required}
        placeholder={placeholder}
        components={components}
        isClearable={isClearable}
        value={field.value}
        name={field.name}
        hideIndicator={hideIndicator}
        noOptionsMessage={noOptionsMessage}
      />
      {!hideErrors && <InputErrorMessage name={name} />}
    </div>
  );
};

export interface IDropdownOption {
  label: string;
  value: string | number;
}
